import { useContext } from 'react';
import { MapEntityDrawerContext } from 'features/mapping/components/MapEntityDrawer';
import {
  getTabsScrollTop,
  TabsScrollTopArgs,
} from 'features/geoEvents/geoEvent.utils';

// Delay so tab content can render before we measure and scroll.
const SCROLL_DELAY = 100;
// Margin subtracted from the evac-info block height when computing scroll.
const EVAC_INFO_MARGIN = 24;

type UseMapEntityDetailsTabsArgs = {
  /**
   * Base scroll offset, tuned per entity type to the sticky header height
   * (e.g. wildfire 240, flooding 175, hazard 120). Not shared - kept per-page.
   */
  baseTop: number;
  containerRefs?: TabsScrollTopArgs['containerRefs'];
  /** Extra offset for a native-only element (e.g. notification button). */
  nativeOffset?: number;
  evacInfoMargin?: number;
};

/**
 * Encapsulates the "scroll the tab bar into view on tab change" behavior shared
 * by the geo event detail pages.
 */
export const useMapEntityDetailsTabs = (
  args: UseMapEntityDetailsTabsArgs,
): { onChangeTab: () => void } => {
  const {
    baseTop,
    containerRefs = {},
    nativeOffset,
    evacInfoMargin = EVAC_INFO_MARGIN,
  } = args;
  const { scrollElementRef } = useContext(MapEntityDrawerContext);

  const onChangeTab = (): void => {
    const tabsScrollTop = getTabsScrollTop({
      scrollElementRef,
      containerRefs,
      baseTop,
      nativeOffset,
      evacInfoMargin,
    });

    setTimeout(() => {
      scrollElementRef?.current?.scrollTo({
        behavior: 'smooth',
        top: tabsScrollTop,
      });
    }, SCROLL_DELAY);
  };

  return { onChangeTab };
};
